import { MapBaseLayers } from '@modules/bcm/@shared/components/map/map.component';
import { U2bColumnDefinition } from '@core/components/layout/table/table.types';
import { Filter } from '@core/datafilter/filter';
import { BcmTenantApiClientView } from '@shared/models/bcm-tenant-api-client-view';
import { BcmTenantElectricMeterProviderView } from '@shared/models/bcm-tenant-electric-meter-provider-view';

export enum BcmUserSettingKey {
    Navigation = 'navigation',
    DashboardSidebar = 'SIDEBAR',
    Dashboards = 'DASHBOARDS',
    TableDefaults = 'table_defaults',
    Map = 'map',
    InvoicePositionsForm = 'invoice_positions_form',
    Appearance = 'appearance',
    LastUsedReceiptPrinter = 'last_used_receipt_printer',
    LastUsedInvoiceWordTemplates = 'last_used_invoice_word_templates',
    BerthSchedulerView = 'berth_scheduler_view',
    InvoicePositionsTable = 'invoice_positions_table',
    BetaFeatures = 'betaFeatures',
}

export enum BcmUserTableSettingKey {
    Persons = 'table_persons',
    Companies = 'table_companies',
    TenantRelations = 'table_tenant_relations',
    Products = 'table_products',
    // ProductCategories = 'table_product_categories',
    Boats = 'table_boats',
    Piers = 'table_piers',
    Berths = 'table_berths',
    BerthsScheduler = 'table_berths_scheduler',
    Storages = 'table_storages',
    ElectricMeters = 'table_electric_meters',
    Keys = 'table_keys',
    // Seasons = 'table_seasons',
    Orders = 'table_order',
    Invoices = 'table_invoices',
    FinancialRecords = 'table_financial_records',
    FinancialRecordsWidget = 'table_financial_records_widget',
    // InvoicesCollections = 'table_invoice_collections',
    Mails = 'table_mails',
    // Mailings = 'table_mailings',
    // MailingTemplates = 'table_mailing_templates',
    // MailingRecipients = 'table_mailing_recipients',
    // Users = 'table_users',
    Documents = 'table_documents',
    Contracts = 'table_contract',
    // ContractTemplates = 'table_contract_templates',
    // ContractTemplatesWord = 'table_contract_templates_word',
    ContractAttachments = 'table_contract_attachments',
    Vouchers = 'table_vouchers',
    CostCenters = 'cost_centers',
    Absences = 'absences',
    Bookings = 'table_bookings',
    Departments = 'departments',
    Employees = 'table_employees',
    Quotes = 'table_quotes',
    BcmTenantApiClientView = 'table_bcm_tenant_api_client_view',
    BcmTenantElectricMeterProviderView = 'table_bcm_tenant_electric_meter_provider_view',
    BoatOrders = 'table_boat_orders',
    BoatDocuments = 'table_boat_documents',
    ClientOrders = 'table_client_orders',
    AdminMasterErrors = 'admin_master_errors',
}

export interface BcmUserSettingsSection {
    [key: string]: any;
}

export interface BcmUserNavigationSettings extends BcmUserSettingsSection {
    state: any;
}

export interface BcmUserDashboardSidebarSettings extends BcmUserSettingsSection {
    SIDEBAR_OPEN: boolean;
}

export interface BcmUserDashboardsSettings extends BcmUserSettingsSection {
    dashboards: { name: string, id: string }[];
}

export interface BerthLabelSettings {
    fontSize: number;
    /** Line break after N characters; 0 disables wrapping. */
    wrapAfterChars: number;
}

/** #6918: Default for berth tile labels on the graphical berth plan (editor + live map). */
export const DEFAULT_BERTH_LABEL_SETTINGS: BerthLabelSettings = {
    fontSize: 6,
    wrapAfterChars: 0,
};

export interface BcmUserMapSettings extends BcmUserSettingsSection {
    selectedGoogleLayer: MapBaseLayers;
    defaultBearing?: number;
    defaultZoom?: number;
    /** Saved map camera center (pan) for the graphical berth plan. */
    defaultCenter?: { lat: number; lng: number };
    berthLabel?: BerthLabelSettings;
}

export interface BcmUserInvoicePositionsFormSettings extends BcmUserSettingsSection {
    vestingPeriodFromDefaultsToToday: boolean;
    vestingPeriodUntilDefaultsToToday: boolean;
    /** Kostenstellen-Dialog bei Produktauswahl in der Schnelleingabe (Rechnungsformular). */
    promptCostCenterPerPosition?: boolean;
    /** Gleiche Artikelnummern in einer Position zusammenfassen (Menge addieren). */
    mergeSameItemNumberPositions?: boolean;
    /** Nach Schnelleingabe Positionsdetailmaske öffnen (Fokus Menge, Enter speichert). */
    openPositionDetailAfterAdd?: boolean;
}

export interface BcmUserInvoicePositionsTableSettings extends BcmUserSettingsSection {
    showVestingPeriodColumns: boolean;
}

export interface BcmUserLastUsedReceiptPrinterSettings extends BcmUserSettingsSection {
    printerId: number | null;
}


export interface BcmUserTableDefaultSettings extends BcmUserSettingsSection {
    rowHeight?: 's' | 'm' | 'l';
    fontSize?: 's' | 'm' | 'l';
}

export interface BcmUserTablesSettings extends BcmUserSettingsSection {
    tableId: string;
    columnDefinitions: U2bColumnDefinition[];
    activeSortColumn?: string;
    pageSize?: number;
}

export interface BcmUserTableFilterSettings extends BcmUserSettingsSection {
    filters?: Filter<unknown>[];
    lastActiveFilterName?: string;
}

export interface BcmUserAppearanceSettings extends BcmUserSettingsSection {
    language: string;
}

export interface BcmUserBerthSchedulerViewSettings extends BcmUserSettingsSection {
    viewType?: number;
}

export interface BcmUserBetaFeaturesSettings extends BcmUserSettingsSection {
    bookingDialogV2?: boolean;
    pricingV2?: boolean;
}

export interface BcmUserLastUsedReceiptPrinterSettings extends BcmUserSettingsSection {
    printerId: number | null;
}

/** Zuletzt im Belegformular gewählte Word-/Legacy-Vorlage pro Belegtyp (pro User). */
export interface BcmUserLastUsedInvoiceWordTemplatesSettings extends BcmUserSettingsSection {
    byDocumentType?: Partial<Record<string, { templateId: number }>>;
}

export interface BcmUserSettings {
    [BcmUserSettingKey.Navigation]: BcmUserNavigationSettings;
    [BcmUserSettingKey.DashboardSidebar]: BcmUserDashboardSidebarSettings;
    [BcmUserSettingKey.Dashboards]: BcmUserDashboardsSettings;
    [BcmUserSettingKey.Map]: BcmUserMapSettings;
    [BcmUserSettingKey.TableDefaults]: BcmUserTableDefaultSettings;
    [BcmUserSettingKey.InvoicePositionsForm]: BcmUserInvoicePositionsFormSettings;
    [BcmUserSettingKey.LastUsedReceiptPrinter]: BcmUserLastUsedReceiptPrinterSettings;
    [BcmUserSettingKey.LastUsedInvoiceWordTemplates]?: BcmUserLastUsedInvoiceWordTemplatesSettings;
    [BcmUserSettingKey.Appearance]: BcmUserAppearanceSettings;
    [BcmUserSettingKey.BerthSchedulerView]?: BcmUserBerthSchedulerViewSettings;
    [BcmUserSettingKey.InvoicePositionsTable]?: BcmUserInvoicePositionsTableSettings;
    [BcmUserSettingKey.BetaFeatures]?: BcmUserBetaFeaturesSettings;
}
